const ISSUER = 'https://auth.openai.com'
const CLIENT_ID = 'app_EMoamEEZ73f0CkXaXp7hrann'
const STORAGE_KEY = 'cfg-codex-auth'

export const codexAuth = $state({account: null, pending: false, code: '', error: ''})
let loginController
let refreshPromise

export function getCodexCredentials() {
  try {
    const credentials = JSON.parse(localStorage.getItem(STORAGE_KEY))
    if (credentials?.accessToken && credentials.refreshToken && credentials.accountId && credentials.expiresAt) return credentials
  } catch {}
  return null
}

function jwtClaims(token) {
  try {
    const encoded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const bytes = Uint8Array.from(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')), c => c.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(bytes))
  } catch {
    return {}
  }
}

function notifyCredentials() {
  const credentials = getCodexCredentials()
  codexAuth.account = credentials ? {email: credentials.email, id: credentials.accountId} : null
  window.dispatchEvent(new Event('codex-auth-change'))
}

function saveTokens(tokens, previous) {
  const claims = jwtClaims(tokens.id_token || tokens.access_token)
  const accessClaims = jwtClaims(tokens.access_token)
  const accountId = claims['https://api.openai.com/auth']?.chatgpt_account_id ||
    accessClaims['https://api.openai.com/auth']?.chatgpt_account_id || previous?.accountId
  const refreshToken = tokens.refresh_token || previous?.refreshToken
  if (!tokens.access_token || !refreshToken || !accountId) throw new Error('OpenAI returned incomplete login credentials. Please sign in again.')
  const credentials = {
    accessToken: tokens.access_token,
    refreshToken,
    accountId,
    expiresAt: tokens.expires_in ? Date.now() + Number(tokens.expires_in) * 1000 : (accessClaims.exp || 0) * 1000,
    email: claims.email || accessClaims['https://api.openai.com/profile']?.email || previous?.email || '',
  }
  if (!credentials.expiresAt) throw new Error('OpenAI did not return a token expiry. Please sign in again.')
  localStorage.setItem(STORAGE_KEY, JSON.stringify(credentials))
  notifyCredentials()
  return credentials
}

async function authRequest(path, body, signal, form = false) {
  try {
    return await fetch(`${ISSUER}${path}`, {
      method: 'POST',
      credentials: 'omit',
      headers: {'Content-Type': form ? 'application/x-www-form-urlencoded' : 'application/json'},
      body: form ? new URLSearchParams(body).toString() : JSON.stringify(body),
      signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(30000)]) : AbortSignal.timeout(30000),
    })
  } catch (error) {
    if (error.name === 'TypeError') throw new Error('Could not reach OpenAI login. Check your connection or browser CORS restrictions.')
    throw error
  }
}

function wait(ms, signal) {
  return new Promise((resolve, reject) => {
    signal.throwIfAborted()
    const abort = () => {
      clearTimeout(timer)
      reject(signal.reason)
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', abort)
      resolve()
    }, ms)
    signal.addEventListener('abort', abort, {once: true})
  })
}

export function cancelCodexLogin() {
  loginController?.abort()
  loginController = undefined
  codexAuth.pending = false
  codexAuth.code = ''
}

export async function startCodexLogin() {
  cancelCodexLogin()
  const controller = new AbortController()
  loginController = controller
  const signal = AbortSignal.any([controller.signal, AbortSignal.timeout(15 * 60 * 1000)])
  codexAuth.pending = true
  codexAuth.error = ''
  try {
    const response = await authRequest('/api/accounts/deviceauth/usercode', {client_id: CLIENT_ID}, signal)
    if (!response.ok) throw new Error(`Could not start Codex login (${response.status}). Enable device-code login in ChatGPT settings if required.`)
    const device = await response.json()
    signal.throwIfAborted()
    const code = device.user_code || device.usercode
    if (!device.device_auth_id || !code) throw new Error('OpenAI did not return a device code.')
    codexAuth.code = code
    let interval = Math.max(Number(device.interval) || 5, 1) * 1000 + 3000
    while (true) {
      await wait(interval, signal)
      const poll = await authRequest('/api/accounts/deviceauth/token', {device_auth_id: device.device_auth_id, user_code: code}, signal)
      if (poll.status === 403 || poll.status === 404) continue
      if (poll.status === 429) {
        interval += 5000
        continue
      }
      if (!poll.ok) throw new Error(`Codex authorization failed (${poll.status}). Please try again.`)
      const approved = await poll.json()
      if (!approved.authorization_code || !approved.code_verifier) throw new Error('OpenAI returned an incomplete authorization code.')
      const exchange = await authRequest('/oauth/token', {
        grant_type: 'authorization_code', client_id: CLIENT_ID,
        code: approved.authorization_code, code_verifier: approved.code_verifier,
        redirect_uri: `${ISSUER}/deviceauth/callback`,
      }, signal, true)
      if (!exchange.ok) throw new Error(`Codex token exchange failed (${exchange.status}). Please sign in again.`)
      const tokens = await exchange.json()
      signal.throwIfAborted()
      saveTokens(tokens)
      return
    }
  } catch (error) {
    if (!controller.signal.aborted) codexAuth.error = error.name === 'TimeoutError' ? 'Codex login timed out. Please try again.' : error.message
  } finally {
    if (loginController === controller) cancelCodexLogin()
  }
}

export function signOutCodex() {
  cancelCodexLogin()
  localStorage.removeItem(STORAGE_KEY)
  codexAuth.error = ''
  notifyCredentials()
}

export async function ensureCodexCredentials(force = false) {
  const refresh = async () => {
    const previous = getCodexCredentials()
    if (!previous) throw new Error('Sign in with ChatGPT on the Settings page to use Codex.')
    if (!force && previous.expiresAt > Date.now() + 60000) return previous
    const response = await authRequest('/oauth/token', {
      grant_type: 'refresh_token', client_id: CLIENT_ID, refresh_token: previous.refreshToken,
    }, undefined, true)
    // Logout or a new login may have happened while the refresh was in flight.
    if (getCodexCredentials()?.refreshToken !== previous.refreshToken) {
      const current = getCodexCredentials()
      if (current) return current
      throw new Error('Codex has been signed out. Please sign in again.')
    }
    if (!response.ok) {
      if (response.status === 400 || response.status === 401) {
        signOutCodex()
        codexAuth.error = 'Codex login expired. Please sign in again.'
      }
      throw new Error(`Could not refresh Codex login (${response.status}).${codexAuth.error ? ` ${codexAuth.error}` : ''}`)
    }
    const tokens = await response.json()
    if (getCodexCredentials()?.refreshToken !== previous.refreshToken) throw new Error('Codex login changed during refresh. Please retry.')
    return saveTokens(tokens, previous)
  }
  if (!refreshPromise) {
    refreshPromise = (navigator.locks ? navigator.locks.request('eimi-codex-refresh', refresh) : refresh())
      .finally(() => { refreshPromise = undefined })
  }
  return refreshPromise
}

notifyCredentials()
window.addEventListener('storage', event => {
  if (event.key === STORAGE_KEY || event.key === null) notifyCredentials()
})
